import { useMemo } from "react";
import { Brain, CheckSquare, Square, Wrench } from "lucide-react";
import { useCaseStepTranscript } from "@/api/queries/cases";
import { ApiError } from "@/api/client";
import { baueProtokoll, protokollGrundText } from "./transcriptRender";

// #303 (UC-2): das Arbeitsprotokoll eines Schritts — on-demand, erst beim Aufklappen.
//
// Zwei Gründe für „erst auf Klick": es ist die grösste Nutzlast dieser Ebene, und JEDE
// Einsicht wird serverseitig protokolliert (`case.transcript-read`). Ein Abruf auf Verdacht
// wäre beides — teuer und ein Audit-Eintrag ohne Anlass.

export function StepTranscript({
  caseId,
  stepId,
  offen,
}: {
  caseId: string;
  stepId: string;
  offen: boolean;
}) {
  const q = useCaseStepTranscript(caseId, stepId, offen);
  const ansicht = useMemo(() => baueProtokoll(q.data?.messages ?? []), [q.data]);

  if (!offen) return null;
  if (q.isLoading) return <p className="text-[12px] text-text3">Lade Protokoll…</p>;
  if (q.isError) {
    // 403 ist eine AUSSAGE (Beteiligte sehen rohe Modell-Nachrichten nicht), kein Fehler
    // im technischen Sinn — und muss deshalb anders klingen als eine Störung.
    const verboten = q.error instanceof ApiError && q.error.status === 403;
    return (
      <p className="text-[12px] text-warn" data-testid="transcript-fehler">
        {verboten
          ? "Das Arbeitsprotokoll steht nur dem Inhaber des Vorgangs offen."
          : "Protokoll nicht abrufbar."}
      </p>
    );
  }

  const daten = q.data;
  if (!daten) return null;
  if (!daten.available) {
    return (
      <p className="text-[12px] text-text3" data-testid="transcript-leer">
        {protokollGrundText(daten.grund)}
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="transcript-inhalt">
      {daten.gekuerzt && (
        <p className="text-[12px] text-warn" data-testid="transcript-gekuerzt">
          Gekürzt — nur der spätere Teil ist erhalten.
        </p>
      )}
      <div className="flex flex-wrap gap-x-4 text-[11.5px] text-text3">
        <span>{ansicht.nachrichten} Einträge</span>
        {ansicht.denkSchritte > 0 && (
          <span className="inline-flex items-center gap-1" data-testid="transcript-denkschritte">
            <Brain className="h-3.5 w-3.5" />
            {ansicht.denkSchritte} Denk-Schritte (nicht angezeigt)
          </span>
        )}
      </div>

      {ansicht.eintraege.length === 0 ? (
        <p className="text-[12px] text-text3" data-testid="transcript-nichts-darstellbar">
          Nichts Darstellbares — der Schritt hat nur Denk-Schritte oder Werkzeug-Ergebnisse
          hinterlassen.
        </p>
      ) : (
        <ol className="space-y-1.5">
          {ansicht.eintraege.map((e, i) => {
            if (e.art === "abschnitt") {
              return (
                <li key={i} className="pt-2" data-testid="protokoll-abschnitt">
                  <div className="text-[12.5px] font-semibold text-text">
                    {e.aktiv ?? "Aufgabenliste aktualisiert"}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {e.todos.map((t, j) => (
                      <div key={j} className="flex items-start gap-1.5 text-[11.5px] text-text2">
                        {t.status === "completed" ? (
                          <CheckSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ok" />
                        ) : (
                          <Square
                            className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${
                              t.status === "in_progress" ? "text-accent" : "text-text3"
                            }`}
                          />
                        )}
                        <span className={t.status === "completed" ? "text-text3 line-through" : ""}>
                          {t.content}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-0.5 text-[11px] text-text3">
                    {e.erledigt} von {e.gesamt} erledigt
                  </div>
                </li>
              );
            }
            if (e.art === "werkzeug") {
              return (
                <li
                  key={i}
                  className="flex items-start gap-1.5 text-[12px] text-text2"
                  data-testid="protokoll-werkzeug"
                >
                  <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text3" />
                  <span className="font-mono text-[11.5px] text-text3">{e.name}</span>
                  {e.label && <span className="min-w-0 break-words">{e.label}</span>}
                </li>
              );
            }
            return (
              <li
                key={i}
                className="whitespace-pre-wrap break-words text-[12.5px] text-text"
                data-testid="protokoll-text"
              >
                {e.text}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
